/**
 * Why a sign-in was refused before anyone knew whose it was.
 *
 * The same list the BFF validates against (`apps/api` module
 * `unidentified-refusals`, mirroring audit-service's `RefusalReason`). `other` is
 * what a record carries when the server met a reason it does not name; it is
 * shown, never offered as a filter, because the server refuses to filter by it.
 */
export const FILTERABLE_REFUSAL_REASONS = [
    'domain_not_admitted',
    'provider_mismatch',
    'tenant_mismatch',
    'email_not_verified',
    'guest_identity',
    'assertion_invalid',
    'user_not_found',
    'email_ambiguous',
    'flag_closed',
    'sso_not_admitted',
] as const;

export const REFUSAL_REASONS = [...FILTERABLE_REFUSAL_REASONS, 'other'] as const;

export type FilterableRefusalReason = (typeof FILTERABLE_REFUSAL_REASONS)[number];
export type RefusalReason = (typeof REFUSAL_REASONS)[number];

export const REFUSAL_PROVIDERS = ['google', 'entra'] as const;
export type RefusalProvider = (typeof REFUSAL_PROVIDERS)[number];

const LABELS: Record<RefusalReason, string> = {
    domain_not_admitted: 'Domain not admitted',
    provider_mismatch: 'Provider mismatch',
    tenant_mismatch: 'Tenant mismatch',
    email_not_verified: 'Email not verified',
    guest_identity: 'Guest identity',
    assertion_invalid: 'Invalid assertion',
    user_not_found: 'User not found',
    email_ambiguous: 'Ambiguous email',
    flag_closed: 'Sign-in turned off',
    sso_not_admitted: 'SSO not admitted',
    other: 'Other',
};

export const PROVIDER_LABEL: Record<RefusalProvider, string> = {
    google: 'Google',
    entra: 'Microsoft Entra',
};

export function isFilterableReason(value: string): value is FilterableRefusalReason {
    return (FILTERABLE_REFUSAL_REASONS as readonly string[]).includes(value);
}

export function isProvider(value: string): value is RefusalProvider {
    return (REFUSAL_PROVIDERS as readonly string[]).includes(value);
}

/** A reason a newer server sends is shown as it came, not hidden. */
export function reasonLabel(reason: string): string {
    return Object.hasOwn(LABELS, reason) ? LABELS[reason as RefusalReason] : reason;
}
